// frontend/src/components/layout/Sidebar.js
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import {
    LayoutDashboard, FileText, Plus, Shield, Users, CheckSquare,
    Truck, BarChart3, UserCog, Anchor, Ship, FileCheck, Briefcase,
    ChevronRight, ChevronLeft, CreditCard, Sparkles, FolderOpen,
} from 'lucide-react';

// =========================================================
// ICON MAP
// =========================================================
const iconMap = {
    dashboard: LayoutDashboard,
    quotes: FileText,
    newquote: Plus,
    opencover: Shield,
    agents: Users,
    policies: CheckSquare,
    cargo: Truck,
    reports: BarChart3,
    users: UserCog,
    ports: Anchor,
    'idf-check': FileCheck,
    'vessel-tracker': Ship,
    'rate-cards': CreditCard,
    'clearing-agents': Briefcase,
    'cargo-certificate': FileCheck,
    certificates: FolderOpen,
};

// =========================================================
// NAV CONFIG
// Order: Dashboard → Cargo group → Operations → Reports
// =========================================================
const roleLinks = {
    agent: [
        { to: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
        { to: '/cargo', label: 'Cargo Certificate', icon: 'cargo-certificate' },
        { to: '/cargo/certificates', label: 'My Certificates', icon: 'certificates' },
        { to: '/vessel-tracker', label: 'Vessel Tracking', icon: 'vessel-tracker' },
        { to: '/quotes', label: 'Quotes', icon: 'quotes' },
        { to: '/quotes/new', label: 'New Quote', icon: 'newquote' },
      //==  { to: '/open-covers', label: 'Open Covers', icon: 'opencover' },
      //==  { to: '/open-covers/new', label: 'New Open Cover', icon: 'newquote' },
        { to: '/clearing-agents', label: 'Clearing Agents', icon: 'clearing-agents' },
        { to: '/idf-check', label: 'IDF Insurance Check', icon: 'idf-check' },
      //====  { to: '/policies', label: 'Policies', icon: 'policies' },
        { to: '/reports', label: 'Reports', icon: 'reports' },
    ],
    broker: [
        { to: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
        { to: '/cargo', label: 'Cargo Certificate', icon: 'cargo-certificate' },
        { to: '/cargo/certificates', label: 'My Certificates', icon: 'certificates' },
        { to: '/vessel-tracker', label: 'Vessel Tracking', icon: 'vessel-tracker' },
        { to: '/quotes', label: 'Quotes', icon: 'quotes' },
        { to: '/quotes/new', label: 'New Quote', icon: 'newquote' },
      //==  { to: '/open-covers', label: 'Open Covers', icon: 'opencover' },
       //== { to: '/open-covers/new', label: 'New Open Cover', icon: 'newquote' },
        { to: '/clearing-agents', label: 'Clearing Agents', icon: 'clearing-agents' },
      //==  { to: '/idf-check', label: 'IDF Insurance Check', icon: 'idf-check' },
       //== { to: '/policies', label: 'Policies', icon: 'policies' },
        { to: '/reports', label: 'Reports', icon: 'reports' },
    ],
    direct_client: [
        { to: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
        { to: '/cargo', label: 'Cargo Certificate', icon: 'cargo-certificate' },
        { to: '/cargo/certificates', label: 'My Certificates', icon: 'certificates' },
        { to: '/vessel-tracker', label: 'Vessel Tracking', icon: 'vessel-tracker' },
        { to: '/policies', label: 'My Policies', icon: 'policies' },
        { to: '/idf-check', label: 'IDF Insurance Check', icon: 'idf-check' },
    ],
    underwriter: [
        { to: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
        { to: '/cargo', label: 'Cargo Certificate', icon: 'cargo-certificate' },
        { to: '/cargo/certificates', label: 'My Certificates', icon: 'certificates' },
        { to: '/vessel-tracker', label: 'Vessel Tracking', icon: 'vessel-tracker' },
       //== { to: '/underwriter/rates', label: 'Rate Cards', icon: 'rate-cards' },
        { to: '/quotes', label: 'Quotes', icon: 'quotes' },
        { to: '/open-covers', label: 'Open Covers', icon: 'opencover' },
      //==  { to: '/policies', label: 'Policies', icon: 'policies' },
        { to: '/reports', label: 'Reports', icon: 'reports' },
    ],
    admin: [
        { to: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
        { to: '/cargo', label: 'Cargo Certificate', icon: 'cargo-certificate' },
        { to: '/cargo/certificates', label: 'My Certificates', icon: 'certificates' },
        { to: '/vessel-tracker', label: 'Vessel Tracking', icon: 'vessel-tracker' },
        { to: '/admin/users', label: 'User Management', icon: 'users' },
        { to: '/quotes', label: 'Quotes', icon: 'quotes' },
        { to: '/open-covers', label: 'Open Covers', icon: 'opencover' },
        { to: '/policies', label: 'Policies', icon: 'policies' },
        { to: '/admin/ports', label: 'Port Management', icon: 'ports' },
        { to: '/reports', label: 'Reports', icon: 'reports' },
    ],
};

// =========================================================
// HOOKS
// =========================================================
function useMediaQuery(query) {
    const [matches, setMatches] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia(query).matches : false
    );

    useEffect(() => {
        const mql = window.matchMedia(query);
        const handler = (e) => setMatches(e.matches);
        if (mql.addEventListener) mql.addEventListener('change', handler);
        else mql.addListener(handler);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', handler);
            else mql.removeListener(handler);
        };
    }, [query]);

    return matches;
}

// =========================================================
// MAIN COMPONENT
// =========================================================
export default function Sidebar({ collapsed = false, onToggleCollapse }) {
    const { user } = useSelector((state) => state.auth);
    const isMobile = useMediaQuery('(max-width: 767px)');

    // Auto-collapse on narrow desktops (768px – 1024px), but never on mobile
    const isNarrowDesktop = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
    const effectiveCollapsed = isMobile ? false : (isNarrowDesktop ? true : collapsed);

    const links = roleLinks[user?.role] || roleLinks.agent;

    // ============ Section grouping ============
    const isDashboard = (l) => l.to === '/dashboard';
    const isCargoGroup = (l) =>
        l.to === '/cargo' ||
        l.to === '/cargo/certificates' ||
        l.to === '/vessel-tracker';
    const isReports = (l) => l.to === '/reports';

    const sections = {
        main: links.filter((l) => isDashboard(l) || isCargoGroup(l)),
        operations: links.filter((l) => !isDashboard(l) && !isCargoGroup(l) && !isReports(l)),
        reports: links.filter(isReports),
    };

    // ============ Icon resolver ============
    const getIcon = (iconName) => {
        const Icon = iconMap[iconName] || FileText;
        return <Icon className="w-5 h-5 flex-shrink-0" />;
    };

    // ============ Link renderer ============
    const renderLink = (link) => (
        <NavLink
            key={link.to}
            to={link.to}
            end={link.to === '/cargo' || link.to === '/dashboard'}
            title={effectiveCollapsed ? link.label : undefined}
            aria-label={link.label}
            className={({ isActive }) =>
                `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500/40 ${isActive
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-600/30'
                    : 'text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700/50 hover:text-gray-900 dark:hover:text-white'
                } ${effectiveCollapsed ? 'justify-center' : ''}`
            }
        >
            {({ isActive }) => (
                <>
                    <span
                        className={`flex-shrink-0 transition-transform duration-200 group-hover:scale-110 ${effectiveCollapsed ? 'w-6 h-6' : ''
                            }`}
                    >
                        {getIcon(link.icon)}
                    </span>

                    {!effectiveCollapsed && (
                        <>
                            <span className="flex-1 truncate">{link.label}</span>
                            <ChevronRight
                                className={`w-4 h-4 flex-shrink-0 transition-all ${isActive
                                        ? 'text-white/70'
                                        : 'text-gray-300 dark:text-slate-500 opacity-0 group-hover:opacity-100'
                                    }`}
                            />
                        </>
                    )}

                    {/* Active dot indicator (collapsed mode) */}
                    {effectiveCollapsed && isActive && (
                        <span className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-1.5 h-6 bg-brand-600 dark:bg-green-400 rounded-full shadow-md" />
                    )}
                </>
            )}
        </NavLink>
    );

    // ============ Section header helper ============
    const renderSectionLabel = (label) => {
        if (effectiveCollapsed) return null;
        return (
            <p className="px-3 text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-[0.15em] mb-2">
                {label}
            </p>
        );
    };

    const renderSectionDivider = () => (
        <div className="pt-4 mt-2 border-t border-gray-100 dark:border-slate-700/60" />
    );

    return (
        <aside
            className={`relative flex flex-col h-full bg-white dark:bg-slate-800 shadow-2xl dark:shadow-2xl dark:shadow-black/30 border-r border-gray-200/60 dark:border-slate-700/60 transition-[width] duration-300 ease-in-out ${effectiveCollapsed ? 'w-20' : 'w-64'
                }`}
        >
            {/* ========== COLLAPSE TOGGLE (desktop only) ========== */}
            {!isMobile && onToggleCollapse && (
                <button
                    onClick={onToggleCollapse}
                    aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    className="absolute -right-3 top-20 z-10 w-6 h-6 rounded-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 shadow-md hover:shadow-lg transition-all flex items-center justify-center text-gray-400 hover:text-brand-600 dark:hover:text-green-400 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                >
                    {effectiveCollapsed ? (
                        <ChevronRight className="w-3.5 h-3.5" />
                    ) : (
                        <ChevronLeft className="w-3.5 h-3.5" />
                    )}
                </button>
            )}

            {/* ========== NAVIGATION ========== */}
            <nav
                className={`flex-1 px-3 py-4 overflow-y-auto transition-all duration-300 ${effectiveCollapsed ? 'space-y-3' : 'space-y-5'
                    }`}
                aria-label="Primary"
            >
                {/* ---- MAIN ---- */}
                {renderSectionLabel('Main Menu')}
                <div className={effectiveCollapsed ? 'space-y-2' : 'space-y-1.5'}>
                    {sections.main.map(renderLink)}
                </div>

                {/* ---- OPERATIONS ---- */}
                {sections.operations.length > 0 && (
                    <>
                        {renderSectionDivider()}
                        {renderSectionLabel('Operations')}
                        <div className={effectiveCollapsed ? 'space-y-2' : 'space-y-1.5'}>
                            {sections.operations.map(renderLink)}
                        </div>
                    </>
                )}

                {/* ---- ANALYTICS ---- */}
                {sections.reports.length > 0 && (
                    <>
                        {renderSectionDivider()}
                        {renderSectionLabel('Analytics')}
                        <div className={effectiveCollapsed ? 'space-y-2' : 'space-y-1.5'}>
                            {sections.reports.map(renderLink)}
                        </div>
                    </>
                )}
            </nav>

            {/* ========== FLOATING PARTICLES (decorative) ========== */}
            <div
                className="absolute inset-0 pointer-events-none overflow-hidden"
                aria-hidden="true"
            >
                <Sparkles className="absolute top-20 right-4 w-4 h-4 text-brand-600/10 dark:text-green-400/5" />
                <Sparkles className="absolute bottom-32 left-3 w-6 h-6 text-brand-600/10 dark:text-green-400/5" />
                <Sparkles className="absolute top-1/2 right-2 w-3 h-3 text-brand-600/10 dark:text-green-400/5" />
            </div>
        </aside>
    );
}